import React, { useState, useCallback } from 'react';
import type { Point } from '../types'; // Assuming Point is in ../types
import { MIN_SCALE, MAX_SCALE } from '../constants/floorPlanConstants'; // Ensure these are exported from the constants file

interface UseTouchGesturesProps {
  scale: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  viewOffset: Point;
  setViewOffset: React.Dispatch<React.SetStateAction<Point>>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  getCanvasCoordinates: (clientX: number, clientY: number) => Point;
  // Optional: If single touch drag threshold logic is also moved here later
  // touchDragThreshold: number; 
}

interface UseTouchGesturesReturn {
  isMultiTouching: boolean;
  processTouchStart: (event: React.TouchEvent<HTMLCanvasElement>) => boolean; // Returns true if multi-touch start/continuation was handled
  processTouchMove: (event: React.TouchEvent<HTMLCanvasElement>) => boolean; // Returns true if multi-touch was handled
  processTouchEnd: (event: React.TouchEvent<HTMLCanvasElement>) => boolean;   // Returns true if multi-touch end was handled
}

export const useTouchGestures = ({
  scale,
  setScale,
  viewOffset,
  setViewOffset,
  canvasRef,
  getCanvasCoordinates,
}: UseTouchGesturesProps): UseTouchGesturesReturn => {
  const [isMultiTouching, setIsMultiTouching] = useState(false);
  const [pinchStartInfo, setPinchStartInfo] = useState<{
    distance: number;
    initialScale: number;
    worldMidPointToKeepFixed: Point;
  } | null>(null);
  const [lastPanScreenMidpoint, setLastPanScreenMidpoint] = useState<Point | null>(null);

  const processTouchStart = useCallback((event: React.TouchEvent<HTMLCanvasElement>): boolean => {
    if (event.touches.length >= 2) { // Changed to >= 2 to handle cases where more than 2 fingers might be down initially
      setIsMultiTouching(true);
      const t1 = event.touches[0];
      const t2 = event.touches[1];

      const screenDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const screenMid = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
      
      // getCanvasCoordinates depends on the current scale and viewOffset
      const worldMid = getCanvasCoordinates(screenMid.x, screenMid.y);
      
      setPinchStartInfo({ distance: screenDist, initialScale: scale, worldMidPointToKeepFixed: worldMid });
      setLastPanScreenMidpoint(screenMid);
      return true; // Multi-touch start handled
    } else if (event.touches.length === 1) {
      // If it's a single touch, ensure multi-touch state is false.
      // This might happen if a multi-touch gesture ended, and then a new single touch starts.
      if (isMultiTouching) { // Check current state before setting
        setIsMultiTouching(false);
        setPinchStartInfo(null);
        setLastPanScreenMidpoint(null);
      }
      return false; // Single touch, not handled as multi-touch by this part
    }
    return false; // Default for other cases (e.g., 0 touches, though unlikely in touchstart)
  }, [scale, getCanvasCoordinates, isMultiTouching, setIsMultiTouching, setPinchStartInfo, setLastPanScreenMidpoint]);

  const processTouchMove = useCallback((event: React.TouchEvent<HTMLCanvasElement>): boolean => {
    // Process only if a multi-touch gesture is active (isMultiTouching is true)
    // and there are exactly two touches for pinch/pan.
    // Other multi-touch scenarios (e.g., 3+ fingers) are not explicitly handled for gestures here.
    if (event.touches.length === 2 && isMultiTouching) {
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      const currentScreenMidPoint = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
      const currentScreenDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

      let nextScale = scale;
      let nextViewOffsetX = viewOffset.x;
      let nextViewOffsetY = viewOffset.y;

      if (pinchStartInfo) {
        nextScale = pinchStartInfo.initialScale * (currentScreenDist / pinchStartInfo.distance);
        nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextScale));

        const worldPointToKeepFixed = pinchStartInfo.worldMidPointToKeepFixed;
        
        const canvasEl = canvasRef.current;
        if (!canvasEl) return true; // Indicate handled but couldn't proceed
        const rect = canvasEl.getBoundingClientRect();
        const screenRelativePinchCenterX = currentScreenMidPoint.x - rect.left;
        const screenRelativePinchCenterY = currentScreenMidPoint.y - rect.top;

        nextViewOffsetX = worldPointToKeepFixed.x - (screenRelativePinchCenterX / nextScale);
        nextViewOffsetY = worldPointToKeepFixed.y - (screenRelativePinchCenterY / nextScale);
      }

      if (lastPanScreenMidpoint) {
        const screenDeltaX = currentScreenMidPoint.x - lastPanScreenMidpoint.x;
        const screenDeltaY = currentScreenMidPoint.y - lastPanScreenMidpoint.y;
        
        // Pan logic should apply to the viewOffset *after* pinch adjustment
        nextViewOffsetX -= screenDeltaX / nextScale; 
        nextViewOffsetY -= screenDeltaY / nextScale;
      }
      
      setScale(nextScale);
      setViewOffset({ x: nextViewOffsetX, y: nextViewOffsetY });
      setLastPanScreenMidpoint(currentScreenMidPoint);
      return true; // Multi-touch handled
    }
    return false; // Not a multi-touch event handled by this function
  }, [isMultiTouching, scale, viewOffset, pinchStartInfo, lastPanScreenMidpoint, setScale, setViewOffset, canvasRef]);

  const processTouchEnd = useCallback((event: React.TouchEvent<HTMLCanvasElement>): boolean => {
    // Check if a multi-touch gesture was active and is now ending
    if (isMultiTouching && event.touches.length < 2) {
      setIsMultiTouching(false);
      setPinchStartInfo(null);
      setLastPanScreenMidpoint(null);
      return true; // Multi-touch end handled
    }
    // If it was multi-touch and still is (e.g., 3 fingers to 2)
    if (isMultiTouching && event.touches.length === 2) {
        const t1 = event.touches[0];
        const t2 = event.touches[1];
        setLastPanScreenMidpoint({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });
        return true; // Still multi-touch, updated lastPanScreenMidpoint
    }
    return false; // Not a multi-touch end event handled by this function
  }, [isMultiTouching]);

  return {
    isMultiTouching,
    processTouchStart,
    processTouchMove,
    processTouchEnd,
  };
};
